import { useQueryClient } from "react-query";
import type { DecisionEngineFlowConfig } from "@hightouch/lib/customer-data/decision-engine/types";

import {
  useComputeSendInsightChartsQuery,
  useDeleteSendInsightChartMutation,
  useRenameSendInsightChartMutation,
  useSaveSendInsightChartMutation,
} from "src/graphql";

import type {
  ChartConfig,
  ChartResult,
  GridChartResult,
  LineChartResult,
  SendInsightTab,
} from "./types";

function mergeLineChart(
  result: LineChartResult,
  config: ChartConfig,
): LineChartResult {
  // channel-reach-line series are keyed by (channel, metric) with no
  // experiment dimension, so there's nothing to merge.
  if (config.type === "channel-reach-line") return result;

  const treatmentIdx = result.series.findIndex(
    (s) => s.segments.experiment === "treatment",
  );
  const uniformIdx = result.series.findIndex(
    (s) => s.segments.experiment === "uniform",
  );
  if (treatmentIdx === -1 || uniformIdx === -1) {
    return {
      ...result,
      series: result.series.filter(
        (s) =>
          s.segments.experiment !== "uniform" &&
          s.segments.experiment !== "holdout",
      ),
    };
  }

  const treatment = result.series[treatmentIdx]!;
  const uniform = result.series[uniformIdx]!;
  const uniformByDate = new Map(uniform.points.map((p) => [p.send_date, p]));

  const mergedPoints = treatment.points.map((tp) => {
    const up = uniformByDate.get(tp.send_date);
    if (!up) return tp;
    const numerator = tp.numerator + up.numerator;
    const denominator =
      tp.denominator != null && up.denominator != null
        ? tp.denominator + up.denominator
        : (tp.denominator ?? up.denominator);
    const value =
      denominator != null && denominator > 0
        ? numerator / denominator
        : numerator;
    return { send_date: tp.send_date, value, numerator, denominator };
  });

  // Include uniform-only dates
  for (const up of uniform.points) {
    if (!treatment.points.some((tp) => tp.send_date === up.send_date)) {
      mergedPoints.push(up);
    }
  }
  mergedPoints.sort((a, b) => a.send_date.localeCompare(b.send_date));

  const merged = { ...treatment, points: mergedPoints };
  return {
    ...result,
    series: result.series
      .filter(
        (s, i) =>
          i !== treatmentIdx &&
          i !== uniformIdx &&
          s.segments.experiment !== "holdout",
      )
      .concat(merged),
  };
}

function mergeGridChart(result: GridChartResult): GridChartResult {
  const cellKey = (segments: Record<string, string | number>) =>
    Object.entries(segments)
      .filter(([k]) => k !== "experiment")
      .map(([k, v]) => `${k}=${v}`)
      .join(":");

  const treatmentCells = new Map(
    result.cells
      .filter((c) => c.segments.experiment === "treatment")
      .map((c) => [cellKey(c.segments), c]),
  );

  const mergedKeys = new Set<string>();
  const merged = result.cells
    .filter(
      (c) =>
        c.segments.experiment !== "uniform" &&
        c.segments.experiment !== "holdout",
    )
    .map((c) => {
      if (c.segments.experiment !== "treatment") return c;
      const key = cellKey(c.segments);
      const uc = result.cells.find(
        (u) =>
          u.segments.experiment === "uniform" && cellKey(u.segments) === key,
      );
      if (!uc) return c;
      mergedKeys.add(key);
      const numerator = (c.numerator ?? 0) + (uc.numerator ?? 0);
      const denominator = (c.denominator ?? 0) + (uc.denominator ?? 0);
      const value = denominator > 0 ? numerator / denominator : numerator;
      return { ...c, value, numerator, denominator };
    });

  // Include uniform-only cells as "treatment"
  for (const uc of result.cells.filter(
    (c) => c.segments.experiment === "uniform",
  )) {
    const key = cellKey(uc.segments);
    if (!treatmentCells.has(key)) {
      merged.push({
        ...uc,
        segments: { ...uc.segments, experiment: "treatment" },
      });
    }
  }

  return { ...result, cells: merged };
}

function mergeTreatmentUniform(
  chart: ChartResult,
  config: ChartConfig,
): ChartResult {
  if (chart.type === "line") return mergeLineChart(chart, config);
  return mergeGridChart(chart);
}

export function useSendInsightCharts({
  flowId,
  flowConfig,
  tab,
  lookbackDays,
  mergeTreatmentAndUniform,
}: {
  flowId: string;
  flowConfig: DecisionEngineFlowConfig;
  tab: SendInsightTab;
  lookbackDays: number;
  mergeTreatmentAndUniform: boolean;
}) {
  const queryClient = useQueryClient();
  const tabKey = tab === "fidelity" ? "fidelity_charts" : "comparison_charts";

  const views = flowConfig.send_insight_views;
  const rawList = (views?.[tabKey] as ChartConfig[] | undefined) ?? [];
  const allConfigs: ChartConfig[] = rawList
    .filter((c) => !c.is_deleted)
    .map((c) => ({ ...c, window_days: lookbackDays }));

  const computeQuery = useComputeSendInsightChartsQuery(
    { flowId, charts: allConfigs },
    {
      enabled: allConfigs.length > 0,
      select: (data) =>
        data.computeSendInsightCharts as {
          charts: ChartResult[];
          latestSendDate?: string | null;
        },
    },
  );

  const rawCharts = computeQuery.data?.charts ?? [];
  const configById = new Map(allConfigs.map((c) => [c.id, c]));
  const charts = mergeTreatmentAndUniform
    ? rawCharts.map((chart) => {
        const config = configById.get(chart.id);
        return config ? mergeTreatmentUniform(chart, config) : chart;
      })
    : rawCharts;

  const chartResultsById = new Map<string, ChartResult>(
    charts.map((r) => [r.id, r]),
  );

  const invalidateAll = () =>
    Promise.all([
      queryClient.invalidateQueries(["ComputeSendInsightCharts"]),
      queryClient.invalidateQueries(["DecisionEngine"]),
    ]);

  const deleteMutation = useDeleteSendInsightChartMutation();
  const saveMutation = useSaveSendInsightChartMutation();
  const renameMutation = useRenameSendInsightChartMutation();

  return {
    configs: allConfigs,
    chartResultsById,
    latestSendDate: computeQuery.data?.latestSendDate ?? null,
    isComputing: computeQuery.isLoading,
    error: computeQuery.error,

    deleteChart: async (chartId: string) => {
      await deleteMutation.mutateAsync({ flowId, chartId });
      await invalidateAll();
    },

    saveChart: async (chart: ChartConfig) => {
      await saveMutation.mutateAsync({ flowId, tab, chart });
      await invalidateAll();
    },

    renameChart: async (chartId: string, name: string) => {
      await renameMutation.mutateAsync({ flowId, chartId, name });
      await invalidateAll();
    },

    isDeleting: deleteMutation.isLoading,
    isSaving: saveMutation.isLoading,
  };
}
